import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _classPrivateFieldLooseBase from "@babel/runtime/helpers/esm/classPrivateFieldLooseBase";
import _classPrivateFieldLooseKey from "@babel/runtime/helpers/esm/classPrivateFieldLooseKey";
const _excluded = ["steps", "beforeExit", "beforeEnter", "beforeStart", "beforeFinish"];
import emptyFunction from 'react-utils/emptyFunction';
import invariant from 'react-utils/invariant';
import { NOT_STARTED, DONE, TOUR_EXIT_ACTION } from '../constants/TourStates';
import handleCallback from './handleCallback';
var _usageTracker = /*#__PURE__*/_classPrivateFieldLooseKey("usageTracker");
var _isFinishing = /*#__PURE__*/_classPrivateFieldLooseKey("isFinishing");
export default class DefaultTourHandler {
  constructor(params) {
    Object.defineProperty(this, _usageTracker, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _isFinishing, {
      writable: true,
      value: false
    });
    const {
        key,
        config: {
          steps = [],
          beforeExit = emptyFunction,
          beforeEnter = emptyFunction,
          beforeStart = emptyFunction,
          beforeFinish = emptyFunction
        } = {},
        progressStore
      } = params,
      restConfigs = _objectWithoutPropertiesLoose(params.config, _excluded);
    this.tour = key;
    this.config = Object.assign({
      steps,
      beforeExit,
      beforeEnter,
      beforeStart,
      beforeFinish
    }, restConfigs);
    this.isActive = false;
    this.progressStore = progressStore;
    this._deactivate = this._deactivate.bind(this);
  }
  setUsageTracker(usageTracker) {
    _classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker] = usageTracker;
  }
  _deactivate(callback) {
    const stepKey = this.getStepKey();
    // This prevents calling beforeExit('close') when the tour is completed and the progress store is reset to NOT_STARTED
    // After completing the tour, beforeExit is already called with beforeExit('finish')
    const shouldCallBeforeExit = stepKey !== NOT_STARTED;
    if (_classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker] && shouldCallBeforeExit) {
      _classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker].trackTourClose(this.tour, stepKey);
    }
    return handleCallback(params => shouldCallBeforeExit ? this.config.beforeExit(params) : undefined, () => {
      this.isActive = false;
      if (typeof callback === 'function') {
        callback();
      }
    }, {
      tourKey: this.tour,
      stepKey,
      action: TOUR_EXIT_ACTION.CLOSE
    });
  }
  deactivate(callback) {
    return handleCallback(this._deactivate, () => {
      this.progressStore.deactivate();
      if (typeof callback === 'function') {
        callback();
      }
    }, undefined);
  }
  _activate() {
    const afterEnter = () => {
      this.isActive = true;
      this._broadcastActiveTour(this.tour);
    };
    return handleCallback(this.config.beforeEnter, afterEnter, {
      tourKey: this.tour,
      stepKey: this.getStepKey()
    });
  }
  _broadcastActiveTour(tourKey) {
    this.progressStore.activateTour(tourKey);
  }
  _tryAction() {
    invariant(this.isActive, `This tour ${this.tour} is not active, so it cannot perform that action`);
  }
  subscribeToUpdates(callback) {
    return this.progressStore.subscribe(callback);
  }
  canGoBack() {
    return this.progressStore.canGoBack();
  }
  goBack() {
    this._tryAction();
    this.progressStore.goBack(this.tour);
  }
  getConfig() {
    return this.config;
  }
  getIndex() {
    const step = this.getStepKey();
    return this.config.steps.indexOf(step);
  }
  getStepKey() {
    return this.progressStore.getStep();
  }
  updateStep(stepKey) {
    this._tryAction();
    if (_classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker] && stepKey !== DONE) {
      _classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker].trackTourViewStep(this.tour, stepKey);
    }
    this.progressStore.updateProgress(stepKey, this.tour);
  }
  startTour(chainable) {
    this._tryAction();
    const {
      beforeStart,
      beforeEnter,
      steps,
      getTourStartStep
    } = this.config;
    if (_classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker]) {
      _classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker].trackTourStart(this.tour);
    }
    const startStep = (getTourStartStep === null || getTourStartStep === void 0 ? void 0 : getTourStartStep({
      tourKey: this.tour,
      stepKey: steps[0],
      steps
    })) || steps[0];
    const goToStartStep = () => this.updateStep(startStep);
    return handleCallback([beforeStart, beforeEnter], goToStartStep, {
      tourKey: this.tour,
      stepKey: startStep
    }, chainable);
  }
  nextStep(stepKey) {
    this._tryAction();
    if (stepKey) {
      this.updateStep(stepKey);
    } else {
      const tourConfig = this.config;
      const currentStepKey = this.getStepKey();
      invariant(currentStepKey !== NOT_STARTED && currentStepKey !== DONE, `The current active tour is ${this.tour}, tour.start() must be called before advancing the tour index`);
      const index = tourConfig.steps.indexOf(currentStepKey);
      const nextStepKey = tourConfig.steps[index + 1];
      if (nextStepKey) {
        this.updateStep(nextStepKey);
      } else {
        this.finishTour();
      }
    }
  }
  finishTour(chainable) {
    this._tryAction();

    // Prevent duplicate finishTour calls, e.g. when beforeFinish callback triggers finishTour again
    if (_classPrivateFieldLooseBase(this, _isFinishing)[_isFinishing]) {
      return;
    }
    _classPrivateFieldLooseBase(this, _isFinishing)[_isFinishing] = true;
    const {
      beforeFinish,
      beforeExit
    } = this.config;
    if (_classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker]) {
      _classPrivateFieldLooseBase(this, _usageTracker)[_usageTracker].trackTourFinish(this.tour);
    }
    const finishTour = () => {
      try {
        this.updateStep(DONE);
      } finally {
        _classPrivateFieldLooseBase(this, _isFinishing)[_isFinishing] = false;
      }
    };
    return handleCallback([beforeFinish, param => beforeExit === null || beforeExit === void 0 ? void 0 : beforeExit(Object.assign({}, param, {
      action: TOUR_EXIT_ACTION.FINISH
    }))], finishTour, {
      tourKey: this.tour,
      stepKey: this.getStepKey()
    }, chainable);
  }
}